import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import {
    ArrowRight,
    ExternalLink,
    CheckCircle,
    AlertTriangle,
    Info,
    Filter,
    Scale,
    X,
    Lock,
    Unlock,
    Shield,
    DollarSign,
    Globe,
    Zap,
    Grid
} from 'lucide-react';
import RAW_CATALOG from '../data/catalogSnapshot.json';
import { enrichCatalog } from '../data/catalogMetadata';

const CATALOG = enrichCatalog(RAW_CATALOG);

// ... (retain existing bias logic and helpers)
// --- Bias / Weighting Logic ---
const BIAS_OPTS = [
    { id: 'freedom', label: 'LIBERTY', desc: 'Prioritize Open Source & Self-Hosting' },
    { id: 'privacy', label: 'PRIVACY', desc: 'Prioritize No-Tracking & Encryption' },
    { id: 'value', label: 'VALUE', desc: 'Prioritize Free/Cheap tiers' },
    { id: 'parity', label: 'PARITY', desc: 'Prioritize Feature Matches' },
];

const GENERIC_TERMS = ['Productivity', 'Business', 'SaaS', 'productivity', 'software', 'app'];

const calculateRelevance = (anchor, candidate) => {
    // 1. Identify overlapping industries excluding generics
    const sharedIndustries = anchor.industries.filter(i =>
        candidate.industries.includes(i) && !GENERIC_TERMS.includes(i)
    );

    // 2. Identify overlapping tags exluding generics
    const sharedTags = anchor.tags.filter(t =>
        candidate.tags.includes(t) && !GENERIC_TERMS.includes(t)
    );

    // 3. Strict Scoring
    // Must have at least one meaningful industry connection OR multiple significant tag connections
    let relevanceScore = 0;
    relevanceScore += sharedIndustries.length * 20;
    relevanceScore += sharedTags.length * 10;

    // Bonus: If they share the PRIMARY industry (0-index), big boost
    if (anchor.industries[0] === candidate.industries[0] && !GENERIC_TERMS.includes(anchor.industries[0])) {
        relevanceScore += 30;
    }

    return relevanceScore;
};

const calculateScore = (anchor, candidate, relevance) => {
    let score = relevance; // Start with the raw relevance base

    // Multipliers based on strict advantages
    // 1. Open Source Advantage
    if (candidate.isOpenSource && !anchor.isOpenSource) score += 30;

    // 2. Pricing Advantage
    if ((candidate.pricing === 'free' || candidate.pricing === 'freemium') && anchor.pricing === 'paid') score += 25;

    // 3. Privacy Advantage
    if (candidate.tags.includes('privacy') && !anchor.tags.includes('privacy')) score += 20;

    return score;
};

// --- Helper Functions for UI Context ---
const getRatingLabel = (rating) => {
    if (rating >= 4.8) return "Exceptional";
    if (rating >= 4.5) return "Superb";
    if (rating >= 4.0) return "Solid";
    return "Niche";
};

const getRatingColor = (rating) => {
    if (rating >= 4.8) return "bg-[var(--signal-green)]";
    if (rating >= 4.5) return "bg-[var(--signal-green)] opacity-80";
    return "bg-[var(--fg-secondary)]";
}


const FinderPage = () => {
    const { domainId } = useParams(); // Capture dynamic domain parameter
    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = useState('');

    // Initialize category with domainId if present, else fallback to generic logic
    const [category, setCategory] = useState(domainId || searchParams.get('category') || 'all');

    // Update category when URL changes
    useEffect(() => {
        if (domainId) {
            setCategory(domainId);
        } else {
            setCategory(searchParams.get('category') || 'all');
        }
    }, [domainId, searchParams]);


    // FILTERS & RANKING STATE
    useEffect(() => {
        document.title = domainId
            ? `AltFinder / ${domainId.charAt(0).toUpperCase() + domainId.slice(1)}`
            : "AltFinder / Search Index";
    }, [domainId]);

    const [anchor, setAnchor] = useState(null);
    const [filters, setFilters] = useState({
        pricing: 'all', // all, free, freemium, paid
        license: 'all', // all, opensource, proprietary
        mobile: false,  // true/false
        origin: 'all',  // all, us, eu, cn...
        compliance: [], // list of strings
        ai: false,      // true/false
        privacy: false  // true/false (no tracking)
    });

    const handleSearch = (val) => setQuery(val);

    // CORE FILTER LOGIC
    const filteredProducts = useMemo(() => {
        return CATALOG.filter(p => {
            const q = query.toLowerCase();

            // 1. Text Search
            const matchQuery = !query || p.name.toLowerCase().includes(q) || p.tags.some(t => t.includes(q)) || p.industries.some(i => i.toLowerCase().includes(q));
            if (!matchQuery) return false;

            // 2. Category Search
            let matchCat = true;
            if (category === 'dev') matchCat = p.industries.some(i => i.includes('Dev') || i.includes('Code') || i.includes('Version') || i.includes('Software'));
            if (category === 'prod') matchCat = p.industries.some(i => i.includes('Productivity') || i.includes('Project') || i.includes('Notes'));
            if (category === 'privacy') matchCat = p.tags.includes('privacy') || p.id === 'tor' || p.id === 'signal';
            if (category === 'design') matchCat = p.industries.some(i => i.includes('Design') || i.includes('Creative'));
            if (category === 'comms') matchCat = p.industries.some(i => i.includes('Communication') || i.includes('Chat') || i.includes('Video') || i.includes('Social'));
            if (category === 'finance') matchCat = p.industries.some(i => i.includes('Finance') || i.includes('Accounting'));
            if (category === 'social') matchCat = p.industries.includes('media');
            if (category === 'recommended') matchCat = p.rating >= 4.5;
            // Handle specific domain categories from DomainsPage
            if (category !== 'all' && category !== 'recommended' && !['dev', 'prod', 'privacy', 'design', 'comms', 'finance', 'social'].includes(category)) {
                matchCat = p.industries.includes(category.replace(/^\w/, c => c.toUpperCase())); // Simple capitalization check for now
                if (!matchCat) matchCat = p.industries.some(i => i.toLowerCase() === category.toLowerCase());
            }

            if (!matchCat) return false;

            // 3. Advanced Filters
            //  - Pricing
            if (filters.pricing !== 'all' && p.pricing !== filters.pricing) return false;

            //  - License (New)
            if (filters.license !== 'all') {
                if (filters.license === 'opensource' && !p.isOpenSource) return false;
                if (filters.license === 'proprietary' && p.isOpenSource) return false;
            }

            //  - Mobile
            if (filters.mobile) {
                const hasMobile = p.platforms.some(pl => ['iOS', 'Android'].includes(pl));
                if (!hasMobile) return false;
            }

            //  - Origin
            if (filters.origin !== 'all') {
                if (!p.countries.includes(filters.origin)) return false;
            }

            //  - Compliance (Must have ALL selected)
            if (filters.compliance.length > 0) {
                const hasAll = filters.compliance.every(c => p.compliance.includes(c));
                if (!hasAll) return false;
            }

            //  - AI
            if (filters.ai) {
                const isAI = p.tags.includes('ai') || p.features.some(f => f.toLowerCase().includes('ai') || f.toLowerCase().includes('generative'));
                if (!isAI) return false;
            }

            //  - Privacy/No Tracking
            if (filters.privacy) {
                if (!p.tags.includes('privacy') && !p.features.some(f => f.toLowerCase().includes('encryption'))) return false;
            }

            return true;
        });
    }, [query, category, filters]);

    // RANKING LOGIC (Scoring against Anchor + Filters)
    const rankedAlternatives = useMemo(() => {
        if (!anchor) return [];
        return filteredProducts
            .filter(p => p.id !== anchor.id)
            .map(p => {
                const relevance = calculateRelevance(anchor, p);
                return {
                    ...p,
                    relevance,
                    score: calculateScore(anchor, p, relevance)
                };
            })
            .filter(p => p.relevance > 0)
            .sort((a, b) => b.score - a.score);
    }, [anchor, filters, filteredProducts]);




    return (
        <AppLayout
            onSearch={handleSearch}
            currentCategory={category}
            onCategoryChange={(newCat) => {
                // If user uses general filter, clear domain path by pushing to generic index
                if (domainId) {
                    setSearchParams({ category: newCat });
                    // Ideally navigate to /index?category=.. but for now keeping it simple or handling it in AppLayout
                    // Letting AppLayout handle navigation via its own logic is safer if possible,
                    // but since state is local here, we might need a redirect.
                    // For now, simple state update:
                    setCategory(newCat);
                } else {
                    setCategory(newCat);
                }
            }}
        >
            <div className="flex h-full flex-col bg-[var(--bg-main)]">

                {/* DOMAIN HEADER (New Feature) */}
                {domainId && !anchor && (
                    <div className="h-32 border-b border-[var(--border-main)] flex flex-col justify-center px-8 bg-[var(--bg-main)]">
                        <div className="flex items-center gap-3 mb-2">
                            <Grid size={16} className="text-[var(--signal-green)]" />
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--fg-secondary)]">Domain Index</span>
                        </div>
                        <h1 className="text-4xl font-serif text-[var(--fg-primary)] capitalize mb-2">{domainId.replace('-', ' ')}</h1>
                        <p className="text-sm text-[var(--fg-secondary)] max-w-2xl">
                            Curated index of software and tools specifically categorized under the {domainId} domain.
                        </p>
                    </div>
                )}

                {/* Filter Control Panel - "The Control Deck" */}
                {!anchor && (
                    <div className="h-16 border-b border-[var(--border-main)] flex items-center bg-[var(--bg-panel)] overflow-x-auto no-scrollbar">

                        {/* Control Group 1: License (New) */}
                        <div className="flex flex-col justify-center px-6 border-r border-[var(--border-main)] h-full min-w-[140px]">
                            <label className="text-[9px] font-mono uppercase text-[var(--fg-tertiary)] tracking-widest mb-1.5 flex items-center gap-2">
                                <Scale size={10} />
                                Licensing
                            </label>
                            <select
                                value={filters.license}
                                onChange={(e) => setFilters(prev => ({ ...prev, license: e.target.value }))}
                                className="bg-transparent text-xs font-mono text-[var(--fg-primary)] outline-none cursor-pointer hover:text-[var(--fg-secondary)] transition-colors uppercase"
                            >
                                <option value="all">Any License</option>
                                <option value="opensource">Open Source</option>
                                <option value="proprietary">Proprietary</option>
                            </select>
                        </div>

                        {/* Control Group 2: Pricing */}
                        <div className="flex flex-col justify-center px-6 border-r border-[var(--border-main)] h-full min-w-[140px]">
                            <label className="text-[9px] font-mono uppercase text-[var(--fg-tertiary)] tracking-widest mb-1.5 flex items-center gap-2">
                                <DollarSign size={10} />
                                Pricing
                            </label>
                            <select
                                value={filters.pricing}
                                onChange={(e) => setFilters(prev => ({ ...prev, pricing: e.target.value }))}
                                className="bg-transparent text-xs font-mono text-[var(--fg-primary)] outline-none cursor-pointer hover:text-[var(--fg-secondary)] transition-colors uppercase"
                            >
                                <option value="all">Any Model</option>
                                <option value="free">Totally Free</option>
                                <option value="freemium">Freemium</option>
                                <option value="paid">Commercial</option>
                            </select>
                        </div>

                        {/* Control Group 3: Origin */}
                        <div className="flex flex-col justify-center px-6 border-r border-[var(--border-main)] h-full min-w-[140px]">
                            <label className="text-[9px] font-mono uppercase text-[var(--fg-tertiary)] tracking-widest mb-1.5 flex items-center gap-2">
                                <Globe size={10} />
                                Origin
                            </label>
                            <select
                                value={filters.origin}
                                onChange={(e) => setFilters(prev => ({ ...prev, origin: e.target.value }))}
                                className="bg-transparent text-xs font-mono text-[var(--fg-primary)] outline-none cursor-pointer hover:text-[var(--fg-secondary)] transition-colors uppercase"
                            >
                                <option value="all">Global (Any)</option>
                                <option value="United States">USA / S.Valley</option>
                                <option value="Germany">DEU / Europe</option>
                                <option value="Switzerland">CHE / Secure</option>
                                <option value="China">CHN / Asia</option>
                            </select>
                        </div>

                        {/* Control Group 4: Features (Toggles) */}
                        <div className="flex items-center gap-1 px-4 h-full">
                            <button
                                onClick={() => setFilters(prev => ({ ...prev, mobile: !prev.mobile }))}
                                className={`h-8 px-3 text-[10px] font-mono border transition-all uppercase tracking-wider flex items-center gap-2 ${filters.mobile ? 'bg-[var(--fg-primary)] text-[var(--bg-main)] border-[var(--fg-primary)]' : 'text-[var(--fg-secondary)] border-transparent hover:border-[var(--border-main)]'}`}
                            >
                                {filters.mobile && <CheckCircle size={10} />}
                                Mobile
                            </button>
                            <button
                                onClick={() => setFilters(prev => ({ ...prev, ai: !prev.ai }))}
                                className={`h-8 px-3 text-[10px] font-mono border transition-all uppercase tracking-wider flex items-center gap-2 ${filters.ai ? 'bg-[var(--fg-primary)] text-[var(--bg-main)] border-[var(--fg-primary)]' : 'text-[var(--fg-secondary)] border-transparent hover:border-[var(--border-main)]'}`}
                            >
                                {filters.ai && <CheckCircle size={10} />}
                                AI Features
                            </button>
                            <button
                                onClick={() => setFilters(prev => ({ ...prev, privacy: !prev.privacy }))}
                                className={`h-8 px-3 text-[10px] font-mono border transition-all uppercase tracking-wider flex items-center gap-2 ${filters.privacy ? 'bg-[var(--fg-primary)] text-[var(--bg-main)] border-[var(--fg-primary)]' : 'text-[var(--fg-secondary)] border-transparent hover:border-[var(--border-main)]'}`}
                            >
                                {filters.privacy && <CheckCircle size={10} />}
                                No Tracking
                            </button>
                        </div>

                        <div className="flex-1"></div>

                        {/* Status Indicator */}
                        <div className="px-6 border-l border-[var(--border-main)] h-full flex flex-col justify-center items-end opacity-50">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-1.5 h-1.5 bg-[var(--signal-green)] rounded-full animate-pulse"></div>
                                <span className="text-[9px] font-mono uppercase text-[var(--fg-primary)] tracking-widest">Connected</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content - The Split */}
                <div className="flex-1 flex overflow-hidden">

                    {/* LEFT PANEL: The Index (Anchor Selection) */}
                    <div className={`
                        flex flex-col border-r border-[var(--border-main)] transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]
                        ${anchor ? 'w-[40%] hidden lg:flex' : 'w-full'}
                    `}>
                        {!anchor ? (
                            // LIST VIEW
                            <div className="flex-1 overflow-y-auto">
                                {/* Header Row */}
                                <div className="sticky top-0 z-10 grid grid-cols-12 gap-4 px-8 py-3 bg-[var(--bg-main)] border-b border-[var(--border-main)] text-[9px] font-mono uppercase tracking-widest text-[var(--fg-tertiary)]">
                                    <div className="col-span-1">ID</div>
                                    <div className="col-span-4">Entity & Context</div>
                                    <div className="col-span-3">License Info</div>
                                    <div className="col-span-3">Rating Score</div>
                                    <div className="col-span-1 text-right"></div>
                                </div>

                                <div className="flex flex-col">
                                    {filteredProducts.map((p, idx) => (
                                        <div
                                            key={p.id}
                                            onClick={() => setAnchor(p)}
                                            className="group relative grid grid-cols-12 gap-4 px-8 py-4 border-b border-[var(--border-main)] cursor-pointer hover:bg-[var(--bg-panel)] transition-colors items-center"
                                        >
                                            {/* THE UNIQUE MOTIF: Relevance Line (only for high rated or recommended items) */}
                                            {p.rating >= 4.5 && (
                                                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[var(--signal-green)] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                            )}

                                            <div className="col-span-1 font-mono text-xs text-[var(--fg-tertiary)]">
                                                {(idx + 1).toString().padStart(2, '0')}
                                            </div>
                                            <div className="col-span-4">
                                                <h3 className="font-sans font-medium text-base text-[var(--fg-primary)] mb-0.5 group-hover:text-[var(--fg-primary)] transition-colors">
                                                    {p.name}
                                                </h3>
                                                <p className="text-[10px] text-[var(--fg-secondary)] truncate pr-4">
                                                    {p.industries[0]} // {p.description.substring(0, 30)}...
                                                </p>
                                            </div>
                                            <div className="col-span-3 flex flex-col justify-center">
                                                <span className={`text-[10px] font-sans ${p.isOpenSource ? 'text-[var(--signal-green)]' : 'text-[var(--fg-secondary)]'}`}>
                                                    {p.isOpenSource ? 'Open Source' : 'Proprietary'}
                                                </span>
                                                <span className="text-[9px] text-[var(--fg-tertiary)] capitalize">{p.pricing}</span>
                                            </div>
                                            <div className="col-span-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-mono text-sm font-medium text-[var(--fg-primary)]">{p.rating.toFixed(1)}</span>
                                                    <span className="text-[10px] text-[var(--fg-tertiary)] hidden sm:inline-block">{getRatingLabel(p.rating)}</span>
                                                </div>
                                                {/* Visual: Rating Bar */}
                                                <div className="w-16 h-1 bg-[var(--border-main)] rounded-full overflow-hidden">
                                                    <div className={`h-full ${getRatingColor(p.rating)}`} style={{ width: `${(p.rating / 5) * 100}%` }}></div>
                                                </div>
                                            </div>
                                            <div className="col-span-1 text-right">
                                                <ArrowRight size={14} className="ml-auto text-[var(--fg-tertiary)] group-hover:text-[var(--fg-primary)] opacity-0 group-hover:opacity-50 transition-all -translate-x-2 group-hover:translate-x-0" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            // ANCHOR DETAIL VIEW (When in split mode on large screens)
                            <div className="flex flex-col h-full bg-[var(--bg-main)] relative p-8">
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        <div className="text-[10px] font-mono text-[var(--fg-tertiary)] uppercase tracking-widest mb-2">Base Comparison</div>
                                        <h2 className="font-sans text-3xl font-medium text-[var(--fg-primary)]">{anchor.name}</h2>
                                    </div>
                                    <button onClick={() => setAnchor(null)} className="p-2 hover:bg-[var(--bg-panel)] rounded-full">
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="space-y-4 text-sm text-[var(--fg-secondary)]">
                                    <div className="flex justify-between border-b border-[var(--border-main)] pb-2">
                                        <span>License</span>
                                        <span className={anchor.isOpenSource ? "text-[var(--signal-green)]" : ""}>{anchor.isOpenSource ? "Open Source" : "Proprietary"}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-[var(--border-main)] pb-2">
                                        <span>Pricing</span>
                                        <span className="capitalize">{anchor.pricing}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* RIGHT PANEL: The Alternatives (Analysis View) */}
                    {anchor && (
                        <div className="flex-1 flex flex-col bg-[var(--bg-panel)] border-l border-[var(--border-main)]">
                            <div className="h-16 border-b border-[var(--border-main)] flex items-center justify-between px-8 bg-[var(--bg-panel)]">
                                <div>
                                    <div className="font-sans text-lg font-medium text-[var(--fg-primary)]">Analysis Results</div>
                                    <div className="text-xs text-[var(--fg-secondary)]">Comparing against {anchor.name}</div>
                                </div>
                                <div className="font-mono text-[10px] text-[var(--fg-tertiary)] bg-[var(--bg-main)] px-2 py-1 rounded">
                                    {rankedAlternatives.length} CANDIDATES
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-8 space-y-6">
                                {rankedAlternatives.map((alt, idx) => (
                                    <div key={alt.id} className="relative group bg-[var(--bg-main)] border border-[var(--border-main)] hover:border-[var(--fg-tertiary)] transition-all p-6 shadow-sm">

                                        <div className="flex justify-between items-start mb-6">
                                            <div>
                                                <h3 className="font-sans text-xl font-medium text-[var(--fg-primary)]">{alt.name}</h3>
                                                <a href={alt.website} target="_blank" className="text-xs text-[var(--fg-tertiary)] hover:text-[var(--fg-secondary)] hover:underline mt-1 flex items-center gap-1">
                                                    {alt.website} <ExternalLink size={10} />
                                                </a>
                                            </div>
                                            <div className="text-right">
                                                <div className="flex items-center gap-2 justify-end mb-1">
                                                    <span className="text-2xl font-sans font-light text-[var(--fg-primary)]">{Math.round(alt.score)}</span>
                                                    <span className="text-[10px] font-mono text-[var(--fg-tertiary)]">/ 100</span>
                                                </div>
                                                <div className="text-[10px] text-[var(--signal-green)] font-medium">Strong Match</div>
                                            </div>
                                        </div>

                                        {/* Friendly "Why Consider This" Section */}
                                        <div className="bg-[var(--bg-panel)] p-4 rounded-sm border border-[var(--border-main)] mb-6 space-y-3">
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--fg-tertiary)] mb-2">Why consider this?</div>

                                            {alt.isOpenSource && !anchor.isOpenSource && (
                                                <div className="flex items-start gap-3">
                                                    <CheckCircle size={14} className="text-[var(--signal-green)] mt-0.5" />
                                                    <div>
                                                        <span className="block text-sm font-medium text-[var(--fg-primary)]">Open Source Transparency</span>
                                                        <span className="block text-xs text-[var(--fg-secondary)]">The code is public and auditable by anyone.</span>
                                                    </div>
                                                </div>
                                            )}
                                            {alt.tags.some(t => t.includes('privacy') && !anchor.tags.includes('privacy')) && (
                                                <div className="flex items-start gap-3">
                                                    <Shield size={14} className="text-[var(--signal-green)] mt-0.5" />
                                                    <div>
                                                        <span className="block text-sm font-medium text-[var(--fg-primary)]">Privacy Focused</span>
                                                        <span className="block text-xs text-[var(--fg-secondary)]">Built to minimize data collection.</span>
                                                    </div>
                                                </div>
                                            )}
                                            {alt.pricing === 'free' && anchor.pricing !== 'free' && (
                                                <div className="flex items-start gap-3">
                                                    <DollarSign size={14} className="text-[var(--signal-green)] mt-0.5" />
                                                    <div>
                                                        <span className="block text-sm font-medium text-[var(--fg-primary)]">Free to use</span>
                                                        <span className="block text-xs text-[var(--fg-secondary)]">No licensing costs for personal use.</span>
                                                    </div>
                                                </div>
                                            )}
                                            {/* Generic Fallback */}
                                            {!alt.isOpenSource && Math.round(alt.score) > 50 && (
                                                <div className="flex items-start gap-3">
                                                    <Zap size={14} className="text-[var(--fg-secondary)] mt-0.5" />
                                                    <div>
                                                        <span className="block text-sm font-medium text-[var(--fg-primary)]">High Feature Parity</span>
                                                        <span className="block text-xs text-[var(--fg-secondary)]">Closely matches the functionality of {anchor.name}.</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <p className="text-sm text-[var(--fg-secondary)] leading-relaxed mb-6">
                                            {alt.description}
                                        </p>

                                        {/* Actions Footer */}
                                        <div className="flex items-center justify-between border-t border-[var(--border-main)] pt-4">
                                            <div className="flex gap-2 flex-wrap">
                                                {alt.tags.slice(0, 3).map(t => (
                                                    <span key={t} className="text-[10px] text-[var(--fg-secondary)] bg-[var(--bg-panel)] px-2 py-1 rounded-sm">
                                                        {t.replace('-', ' ')}
                                                    </span>
                                                ))}
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAnchor(alt);
                                                }}
                                                className="flex items-center gap-2 text-xs font-medium text-[var(--fg-primary)] hover:text-[var(--signal-green)] transition-colors"
                                            >
                                                Use as Baseline <ArrowRight size={12} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AppLayout>
    );
};

export default FinderPage;

